// TinyPNG Image Compression Service
// This service compresses images using TinyPNG API before uploading to Firebase Storage
// Falls back to browser-based compression when TinyPNG is not available due to CORS

import {
  compressImageWithCanvas,
  isBrowserCompressionSupported,
  getOptimalCompressionSettings,
} from './browser-compression.js';

const TINYPNG_API_KEY = process.env.REACT_APP_TINYPNG_API_KEY;
const ENABLE_COMPRESSION = process.env.REACT_APP_ENABLE_COMPRESSION === "true";
const COMPRESSION_QUALITY = parseInt(
  process.env.REACT_APP_COMPRESSION_QUALITY || "80"
);
const MAX_FILE_SIZE_FOR_COMPRESSION = parseInt(
  process.env.REACT_APP_MAX_FILE_SIZE_FOR_COMPRESSION || "5242880"
); // 5MB

/**
 * Checks if compression is available and configured
 */
export const isCompressionAvailable = () => {
  return (
    ENABLE_COMPRESSION &&
    TINYPNG_API_KEY &&
    TINYPNG_API_KEY !== "your_tinypng_api_key_here"
  );
};

/**
 * Checks if a file should be compressed based on type and size
 */
export const shouldCompressFile = (file) => {
  // Can compress if either TinyPNG or browser compression is available
  if (!isCompressionAvailable() && !isBrowserCompressionSupported()) return false;

  // Only compress images
  if (!file.type.startsWith("image/")) return false;

  // Skip SVG files as they're already optimized
  if (file.type === "image/svg+xml") return false;

  // Only compress files smaller than MAX_FILE_SIZE_FOR_COMPRESSION (to avoid API limits)
  if (file.size > MAX_FILE_SIZE_FOR_COMPRESSION) return false;

  // Supported image types (broader support with browser fallback)
  const supportedTypes = ["image/jpeg", "image/png", "image/webp"];
  return supportedTypes.includes(file.type);
};

/**
 * Converts a File to base64 string (reserved for future use)
 */
// eslint-disable-next-line no-unused-vars
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove data:image/jpeg;base64, prefix
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Converts base64 string back to File object (reserved for future use)
 */
// eslint-disable-next-line no-unused-vars
const base64ToFile = (base64Data, filename, mimeType) => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });

  return new File([blob], filename, { type: mimeType });
};

/**
 * Compresses an image using TinyPNG API with browser fallback
 */
export const compressImage = async (file, onProgress = null) => {
  console.log(
    `🔍 Starting compression for: ${file.name} (${formatFileSize(file.size)})`
  );

  if (!shouldCompressFile(file)) {
    console.log(
      `⏭️ Skipping compression for ${file.name}: File doesn't meet compression criteria`
    );
    if (onProgress) onProgress(100);
    return {
      file,
      compressed: false,
      reason: "File doesn't meet compression criteria",
    };
  }

  // First try TinyPNG if available
  if (isCompressionAvailable()) {
    try {
      return await compressWithTinyPNG(file, onProgress);
    } catch (error) {
      console.warn(`⚠️ TinyPNG compression failed for ${file.name}, trying browser fallback:`, error);
      
      // Check if this is a CORS error or TinyPNG unavailable
      const isCorsError = error.message.includes('CORS') || 
                         error.message.includes('Failed to fetch') ||
                         error.message.includes('Network request failed');
      
      if (isCorsError) {
        console.log(`🔄 Falling back to browser compression due to CORS restrictions`);
      }
    }
  }

  // Fallback to browser-based compression
  if (isBrowserCompressionSupported()) {
    try {
      return await compressWithBrowser(file, onProgress);
    } catch (error) {
      console.error(`❌ Browser compression also failed for ${file.name}:`, error);
    }
  }

  // If all compression methods fail, return original file
  console.log(`⚠️ All compression methods failed for ${file.name}, using original file`);
  if (onProgress) onProgress(100);
  
  const originalFile = new File([file], file.name, { type: file.type });
  originalFile.compressed = false;
  originalFile.compressionFailed = true;
  originalFile.compressionError = "All compression methods failed";
  
  return originalFile;
};

/**
 * Compresses an image using TinyPNG API
 */
const compressWithTinyPNG = async (file, onProgress = null) => {
  if (onProgress) onProgress(10);
  console.log(`🚀 Compressing ${file.name} with TinyPNG...`);

  // Call TinyPNG API
  const response = await fetch("https://api.tinify.com/shrink", {
    method: "POST",
    headers: {
      Authorization: `Basic ${btoa(`api:${TINYPNG_API_KEY}`)}`,
    },
    body: file, // Send file directly
  });

  if (onProgress) onProgress(50);

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
      console.error(`❌ TinyPNG API Error:`, errorData);
    } catch (e) {
      console.error(
        `❌ TinyPNG API Error: ${response.status} ${response.statusText}`
      );
    }
    throw new Error(`TinyPNG API error: ${errorMessage}`);
  }

  const result = await response.json();
  console.log(`📊 TinyPNG response:`, result);

  if (onProgress) onProgress(70);

  if (!result.output || !result.output.url) {
    throw new Error("Invalid response from TinyPNG API");
  }

  // Download the compressed image
  console.log(`📥 Downloading compressed image from: ${result.output.url}`);
  const compressedResponse = await fetch(result.output.url);

  if (!compressedResponse.ok) {
    throw new Error(
      `Failed to download compressed image: ${compressedResponse.status}`
    );
  }

  if (onProgress) onProgress(85);

  const compressedArrayBuffer = await compressedResponse.arrayBuffer();
  const compressedBlob = new Blob([compressedArrayBuffer], {
    type: file.type,
  });
  const compressedFile = new File([compressedBlob], file.name, {
    type: file.type,
  });

  if (onProgress) onProgress(100);

  // Calculate compression stats
  const originalSize = file.size;
  const compressedSize = compressedFile.size;
  const savedBytes = originalSize - compressedSize;
  const compressionRatio = Math.round((savedBytes / originalSize) * 100);

  console.log(`✅ TinyPNG compression successful for ${file.name}:`);
  console.log(`   Original: ${formatFileSize(originalSize)}`);
  console.log(`   Compressed: ${formatFileSize(compressedSize)}`);
  console.log(
    `   Saved: ${formatFileSize(savedBytes)} (${compressionRatio}%)`
  );

  // Add compression metadata
  compressedFile.compressed = true;
  compressedFile.compressionStats = {
    originalSize,
    compressedSize,
    savedBytes,
    compressionRatio,
    method: 'tinypng',
  };

  return compressedFile;
};

/**
 * Compresses an image using browser Canvas API
 */
const compressWithBrowser = async (file, onProgress = null) => {
  console.log(`🎨 Compressing ${file.name} with browser Canvas API...`);
  
  if (onProgress) onProgress(10);
  
  const settings = getOptimalCompressionSettings(file);
  
  const compressedFile = await compressImageWithCanvas(file, settings, (progress) => {
    // Map canvas progress to our progress (10-90%)
    const mappedProgress = 10 + (progress * 0.8);
    if (onProgress) onProgress(mappedProgress);
  });

  if (onProgress) onProgress(100);

  console.log(`✅ Browser compression successful for ${file.name}:`);
  console.log(`   Original: ${formatFileSize(compressedFile.compressionStats.originalSize)}`);
  console.log(`   Compressed: ${formatFileSize(compressedFile.compressionStats.compressedSize)}`);
  console.log(
    `   Saved: ${formatFileSize(compressedFile.compressionStats.savedBytes)} (${compressedFile.compressionStats.compressionRatio}%)`
  );

  return compressedFile;
};

/**
 * Compresses multiple images with progress tracking
 */
export const compressImages = async (
  files,
  onOverallProgress = null,
  onFileProgress = null
) => {
  const compressedFiles = [];
  const total = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      const compressedFile = await compressImage(file, (progress) => {
        if (onFileProgress) {
          onFileProgress(i, progress, file.name);
        }
      });

      compressedFiles.push(compressedFile);

      if (onOverallProgress) {
        const overallProgress = ((i + 1) / total) * 100;
        onOverallProgress(overallProgress);
      }
    } catch (error) {
      console.error(`Failed to compress ${file.name}:`, error);
      // Use original file if compression fails
      compressedFiles.push(file);
    }
  }

  return compressedFiles;
};

/**
 * Gets compression statistics for display
 */
export const getCompressionStats = (originalFiles, compressedFiles) => {
  const originalSize = originalFiles.reduce((sum, file) => sum + file.size, 0);
  const compressedSize = compressedFiles.reduce(
    (sum, file) => sum + file.size,
    0
  );
  const savedBytes = originalSize - compressedSize;
  const compressionRatio =
    originalSize > 0 ? (savedBytes / originalSize) * 100 : 0;

  return {
    originalSize,
    compressedSize,
    savedBytes,
    compressionRatio: Math.round(compressionRatio * 10) / 10, // Round to 1 decimal
    compressionEnabled: isCompressionAvailable(),
  };
};

/**
 * Formats file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Export configuration for external use
export const compressionConfig = {
  isEnabled: ENABLE_COMPRESSION,
  hasApiKey:
    !!TINYPNG_API_KEY && TINYPNG_API_KEY !== "your_tinypng_api_key_here",
  quality: COMPRESSION_QUALITY,
  maxFileSize: MAX_FILE_SIZE_FOR_COMPRESSION,
  maxFileSizeFormatted: formatFileSize(MAX_FILE_SIZE_FOR_COMPRESSION),
  // Browser compression info
  browserCompressionAvailable: isBrowserCompressionSupported(),
  anyCompressionAvailable: isCompressionAvailable() || isBrowserCompressionSupported(),
  // Compression method priority
  preferredMethod: isCompressionAvailable() ? 'tinypng' : 'browser',
};
